import {formatDate, formatEuro, moneyInputValue} from '../../core/billing/billing.core.js';
import {escapeHtml} from '../../core/escape-html.js';

/**
 * @typedef {{invoices: import('../../shell/types/billing.types.js').BillingInvoiceApiResponse[]} | {error: string} | null} HistoryState
 *   null while the invoices are loading
 */

/**
 * @param {import('../../shell/types/billing.types.js').BillingInvoiceApiResponse} invoice
 * @param {boolean} latest only the latest invoice of an item can be undone
 * @param {boolean} editingCost whether the cost cell holds the cost correction form
 * @returns {string} one table row
 */
function invoiceRowMarkup(invoice, latest, editingCost) {
    const number = escapeHtml(invoice.invoiceNumber);
    const cost = editingCost
        ? `<form class="history-cost-form" data-invoice-id="${invoice.id}" novalidate>
                <label class="visually-hidden" for="history-cost-${invoice.id}">Cost of invoice ${number}, empty for no cost</label>
                <input id="history-cost-${invoice.id}" class="history-cost-input" type="text" inputmode="decimal" autocomplete="off" placeholder="No cost" value="${moneyInputValue(invoice.cost)}" aria-describedby="history-cost-error-${invoice.id}" aria-invalid="false" />
                <button type="submit" class="btn btn-primary btn-sm">Save</button>
                <button type="button" class="btn btn-outline btn-sm btn-history-cost-cancel">Cancel</button>
                <p id="history-cost-error-${invoice.id}" class="field-error history-cost-error" role="alert" hidden></p>
            </form>`
        : invoice.cost === null || invoice.cost === undefined
            ? '<span class="history-no-cost">No cost</span>'
            : escapeHtml(formatEuro(invoice.cost));
    const editCost = editingCost
        ? ''
        : `<button type="button" class="btn btn-outline btn-sm btn-history-cost-edit" data-invoice-id="${invoice.id}" aria-label="Edit cost of invoice ${number}">
                <i aria-hidden="true" class="fa-solid fa-pen"></i> Edit cost
            </button>`;
    const undo = latest
        ? `<button type="button" class="btn btn-outline btn-sm btn-billing-undo" data-invoice-id="${invoice.id}" aria-label="Undo invoice ${number}">
                <i aria-hidden="true" class="fa-solid fa-rotate-left"></i> Undo
            </button>`
        : '';
    return `<tr data-invoice-id="${invoice.id}">
                <td class="history-number">${number}</td>
                <td>${escapeHtml(formatDate(invoice.invoicedOn))}</td>
                <td>${escapeHtml(formatDate(invoice.periodFrom))} to ${escapeHtml(formatDate(invoice.periodTo))}</td>
                <td class="history-money">${escapeHtml(formatEuro(invoice.amount))}</td>
                <td class="history-money">${cost}</td>
                <td class="history-money">${escapeHtml(formatEuro(invoice.profit))}</td>
                <td class="history-action">${editCost}${undo}</td>
            </tr>`;
}

/**
 * Fills the history panel of one item: a loading text, the load error, an empty text or the
 * invoices newest first, each with Edit cost and the first (latest) one with Undo.
 *
 * @param {HTMLElement} panel the item's `.billing-item-extra`
 * @param {HistoryState} state
 * @param {number|null} [editingInvoiceId] the invoice whose cost is being corrected
 */
export function renderBillingHistory(panel, state, editingInvoiceId = null) {
    if (state === null) {
        panel.innerHTML = '<p class="history-note" role="status">Loading invoices...</p>';
    } else if ('error' in state) {
        panel.innerHTML = `<p class="history-note history-error" role="alert">${escapeHtml(state.error)}</p>`;
    } else if (state.invoices.length === 0) {
        panel.innerHTML = '<p class="history-note">No invoices recorded yet.</p>';
    } else {
        panel.innerHTML = `<table class="history-table">
            <thead><tr>
                <th scope="col">Invoice</th><th scope="col">Invoice date</th><th scope="col">Period</th>
                <th scope="col" class="history-money">Amount</th><th scope="col" class="history-money">Cost</th>
                <th scope="col" class="history-money">Profit</th><th scope="col"><span class="visually-hidden">Actions</span></th>
            </tr></thead>
            <tbody>${state.invoices.map((invoice, index) => invoiceRowMarkup(invoice, index === 0, invoice.id === editingInvoiceId)).join('')}</tbody>
        </table>`;
    }
}
